/**
 * The assistant: runs conversations between you, the model on your computer and the ledger.
 *
 *   1. Your question goes to the model with a short snapshot of the ledger and the tool list.
 *   2. When the model calls a read tool, the browser runs it on your local copy and sends back
 *      the answer. (Up to 6 rounds per question.)
 *   3. When it calls a propose tool, you get a card. Nothing changes until you tap Yes; then the
 *      change goes through the normal outbox, with history and undo like any other edit.
 *
 * Each group keeps any number of chats on this device. The model's reasoning and every tool
 * call (what it asked, what it got back) are kept so you can see how it got its answer.
 */
import {
  TOOL_DEFS,
  guessCategory,
  TUTORING_TOOL_DEFS,
  TUTORING_TOOL_NAMES,
  applyApiEdits,
  applyFieldEdits,
  runTutoringTool,
  MONEY_TOOL_DEFS,
  MONEY_TOOL_NAMES,
  runMoneyTool,
  syncPaymentBalance,
  cardPurchases,
  type MoneyContext,
  type MoneyAccount,
  type MoneyProgram,
  type ApiCall,
  type EditableField,
  computeLedger,
  proposalFields,
  runTool,
  snapshotText,
  type Command,
} from "@split/core";
import { app } from "../store.svelte.ts";
import { today } from "../format.ts";
import { tutoring } from "../tutoring.svelte.ts";
import { api } from "../api.ts";
import { aiQueue } from "./queue.svelte.ts";
import { chat, listModels, LlmError, type ChatMessage, type LlmSettings } from "./llm.ts";

export type UiMessage =
  | { id: string; role: "user"; text: string; llmIndex?: number }
  | {
      id: string;
      role: "assistant";
      text: string;
      reasoning?: string;
      ms?: number;
      streaming?: boolean;
      /** The thinking box is open: open while it thinks, then only the person opens or closes it. */
      thinkingOpen?: boolean;
    }
  | {
      id: string;
      role: "activity";
      text: string;
      failed?: boolean;
      tool?: { name: string; args: string; result: string };
    }
  | {
      id: string;
      role: "proposal";
      summary: string;
      commands: Command[];
      /** Your fixes to its numbers and words, by field key (see proposalFields). */
      edits?: Record<string, string>;
      /** Tutoring changes go to the server instead of the ledger. */
      api?: { calls: ApiCall[]; fields: EditableField[] };
      status: "pending" | "applied" | "declined" | "failed";
      error?: string;
    }
  | { id: string; role: "error"; text: string };

export interface ChatMeta {
  id: string;
  title: string;
  updatedAt: string;
  count: number;
  scope?: ChatScope;
  /** You picked the category yourself: questions no longer change it. */
  scopePicked?: boolean;
}

/** What a chat is about: it decides what the AI can look at and change. */
export type ChatScope = "group" | "tutoring" | "money" | "everything";
export const SCOPES: { id: ChatScope; label: string; hint: string }[] = [
  {
    id: "group",
    label: "Splits",
    hint: "Items, people, payments and receipts in the group picked",
  },
  { id: "tutoring", label: "Tutoring", hint: "Students, tutors, sessions and the calendar" },
  { id: "money", label: "Money", hint: "Your cards, bank accounts and points" },
  { id: "everything", label: "Everything", hint: "All of it at once" },
];

const MAX_ROUNDS = 6;
const KEEP = 200;
const SETTINGS_KEY = "ai:settings";
export const DEFAULT_SETTINGS: LlmSettings = {
  baseUrl: "http://localhost:1234/v1",
  model: "",
  apiKey: "",
};

const SYSTEM = `You are the assistant inside Split Ledger, an app that tracks shared purchases (mostly Costco runs and meals) and who has paid.
- Answer from the ledger. Use the tools to look things up instead of guessing; the snapshot below is a summary only.
- Money is in US dollars; always show cents ($4.99).
- To change anything, call a propose_* tool. That shows the user a card with Yes / No. Never say a change is done until they confirm.
- When the user says who got what on a receipt ("Nick got the water and eggs, Carrie and I split the chicken"), use propose_assign once for the whole receipt.
- If a person or item is ambiguous, ask a short question.
- Tutoring (students, sessions, payments, the calendar) has its own tools: tutoring_overview to look, tutoring_sessions for when lessons were or will be (by tutor, student and dates), propose_log_session / propose_tutoring_payment / propose_add_time to change.
- Never work out a date or a weekday yourself. Use the dates and weekdays a tool gives you, exactly. If no tool can answer, say you can't look that up rather than guessing.
- Your own money (cards, bank accounts, points) has its own tools: money_overview to look, propose_card_payment / propose_balance to change. Suggestions only; it isn't financial advice.
- Keep replies short and plain. Use a short list when comparing several people or items.`;

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function save(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked: the chat still works for this visit */
  }
}

function drop(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

const uid = () => crypto.randomUUID();
const localTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

class Assistant {
  settings = $state<LlmSettings>(load(SETTINGS_KEY, DEFAULT_SETTINGS));
  messages = $state<UiMessage[]>([]);
  chats = $state<ChatMeta[]>([]);
  chatId = $state<string | null>(null);
  /** This chat's scope; new chats start with the space you opened Ask from. */
  scope = $state<ChatScope>("group");
  /** The person picked this chat's category; until then it follows each question. */
  scopePicked = $state(false);
  defaultScope: ChatScope = "group";
  running = $state(false);
  connection = $state<{
    status: "unknown" | "checking" | "ok" | "down";
    models: string[];
    message: string;
  }>({
    status: "unknown",
    models: [],
    message: "",
  });

  /** The conversation as the model sees it (includes tool calls and results). */
  private llm: ChatMessage[] = [];
  private abort: AbortController | null = null;
  private groupId: string | null = null;

  private get indexKey() {
    return `ai:chats:${this.groupId}`;
  }
  private chatKey(id: string) {
    return `ai:chat:${this.groupId}:${id}`;
  }

  /** Loads the open group's chats and its most recent one. */
  open(groupId: string) {
    if (this.groupId === groupId) return;
    this.stop();
    this.groupId = groupId;
    let chats = load<ChatMeta[]>(this.indexKey, []);
    // Chats saved before there were several per group become "Earlier chat".
    const old = load<{ ui: UiMessage[]; llm: ChatMessage[] } | null>(`ai:chat:${groupId}`, null);
    if (old && old.ui.length) {
      const id = uid();
      save(this.chatKey(id), old);
      chats = [
        { id, title: "Earlier chat", updatedAt: new Date().toISOString(), count: old.ui.length },
        ...chats,
      ];
      save(this.indexKey, chats);
    }
    if (old) drop(`ai:chat:${groupId}`);
    this.chats = chats;
    if (chats[0]) this.openChat(chats[0].id);
    else this.newChat();
  }

  openChat(id: string) {
    if (this.running) return;
    const saved = load<{ ui: UiMessage[]; llm: ChatMessage[] }>(this.chatKey(id), {
      ui: [],
      llm: [],
    });
    this.chatId = id;
    this.messages = saved.ui.map((m) => (m.role === "assistant" ? { ...m, streaming: false } : m));
    this.llm = saved.llm;
    const meta = this.chats.find((c) => c.id === id);
    this.scope = meta?.scope ?? "group";
    this.scopePicked = meta?.scopePicked ?? false;
  }

  /** Change what this chat is about (kept with the chat). picked: you chose it yourself. */
  setScope(scope: ChatScope, picked = true) {
    this.scope = scope;
    if (picked) this.scopePicked = true;
    const c = this.chats.find((x) => x.id === this.chatId);
    if (c) {
      c.scope = scope;
      c.scopePicked = this.scopePicked;
      save(this.indexKey, $state.snapshot(this.chats));
    }
  }

  /**
   * Until you pick a category yourself, each question picks it from its wording ("when did
   * Jianing teach" is Tutoring). Says so in the chat when it changes.
   */
  private followQuestion(question: string) {
    if (this.scopePicked || this.scope === "everything") return;
    const allowed = app.role === "owner";
    const guess = guessCategory(question, {
      groups: app.groups.map((g) => g.name),
      tutoring: allowed ? [...tutoring.students, ...tutoring.tutors].map((x) => x.name) : [],
    });
    if (!guess || guess === this.scope || (!allowed && guess !== "group")) return;
    this.setScope(guess, false);
    const label = SCOPES.find((x) => x.id === guess)!.label;
    this.messages.push({
      id: uid(),
      role: "activity",
      text: `Switched to ${label} from your question. Change it at the top.`,
    });
  }

  /** Starts an empty chat (it's only saved once you send something). */
  newChat() {
    if (this.running) this.stop();
    this.chatId = uid();
    this.messages = [];
    this.llm = [];
    this.scope = this.defaultScope;
    this.scopePicked = false;
  }

  deleteChat(id: string) {
    drop(this.chatKey(id));
    this.chats = this.chats.filter((c) => c.id !== id);
    save(this.indexKey, $state.snapshot(this.chats));
    if (this.chatId === id) {
      if (this.chats[0]) this.openChat(this.chats[0].id);
      else this.newChat();
    }
  }

  renameChat(id: string, title: string) {
    const c = this.chats.find((x) => x.id === id);
    if (!c || !title.trim()) return;
    c.title = clip(title.trim(), 60);
    save(this.indexKey, $state.snapshot(this.chats));
  }

  private persist() {
    if (!this.groupId || !this.chatId || this.messages.length === 0) return;
    save(this.chatKey(this.chatId), {
      ui: $state.snapshot(this.messages).slice(-KEEP),
      llm: this.llm.slice(-KEEP),
    });
    const firstQuestion = this.messages.find((m) => m.role === "user");
    const existing = this.chats.find((c) => c.id === this.chatId);
    const meta: ChatMeta = {
      id: this.chatId,
      title:
        existing?.title ??
        clip(firstQuestion && "text" in firstQuestion ? firstQuestion.text : "New chat", 48),
      updatedAt: new Date().toISOString(),
      count: this.messages.length,
      scope: this.scope,
      scopePicked: this.scopePicked,
    };
    this.chats = [meta, ...this.chats.filter((c) => c.id !== this.chatId)];
    save(this.indexKey, $state.snapshot(this.chats));
  }

  saveSettings(next: LlmSettings) {
    this.settings = { ...next, baseUrl: next.baseUrl.trim() || DEFAULT_SETTINGS.baseUrl };
    save(SETTINGS_KEY, this.settings);
  }

  /** Checks the server and fetches its models; picks the first model if none is chosen. */
  async check(): Promise<void> {
    this.connection = { ...this.connection, status: "checking", message: "" };
    try {
      const models = await listModels(this.settings, AbortSignal.timeout(6000));
      if (!this.settings.model && models[0])
        this.saveSettings({ ...this.settings, model: models[0] });
      this.connection = {
        status: models.length ? "ok" : "down",
        models,
        message: models.length
          ? ""
          : "LM Studio is running but no model is loaded. Load one in LM Studio.",
      };
    } catch (e) {
      this.connection = {
        status: "down",
        models: [],
        message:
          e instanceof LlmError
            ? e.message
            : "Can't reach the AI server. Check that LM Studio's server is running.",
      };
    }
  }

  stop() {
    this.abort?.abort();
  }

  /** Clears the open chat's messages (the chat stays in the list). */
  clear() {
    this.stop();
    this.messages = [];
    this.llm = [];
    if (this.chatId) this.deleteChat(this.chatId);
  }

  async send(text: string): Promise<void> {
    const question = text.trim();
    if (!question || this.running) return;
    this.messages.push({ id: uid(), role: "user", text: question, llmIndex: this.llm.length });
    this.followQuestion(question);
    this.llm.push({ role: "user", content: question });
    await this.run();
  }

  /** Asks again from your last question, replacing the answer after it. */
  async regenerate(): Promise<void> {
    if (this.running) return;
    const i = this.lastUserIndex();
    if (i < 0) return;
    const q = this.messages[i] as Extract<UiMessage, { role: "user" }>;
    this.messages = this.messages.slice(0, i + 1);
    this.llm = this.llm.slice(0, (q.llmIndex ?? this.llm.length - 1) + 1);
    await this.run();
  }

  /** Changes your last question and asks it again. */
  async editLast(text: string): Promise<void> {
    if (this.running || !text.trim()) return;
    const i = this.lastUserIndex();
    if (i < 0) return;
    const q = this.messages[i] as Extract<UiMessage, { role: "user" }>;
    const at = q.llmIndex ?? this.llm.length - 1;
    this.messages = this.messages.slice(0, i);
    this.llm = this.llm.slice(0, at);
    await this.send(text);
  }

  private lastUserIndex(): number {
    for (let i = this.messages.length - 1; i >= 0; i--)
      if (this.messages[i]!.role === "user") return i;
    return -1;
  }

  private async run(): Promise<void> {
    return aiQueue.first((id) => this.runNow(id), "Ask");
  }

  private job = "";
  private async runNow(job = ""): Promise<void> {
    this.job = job;
    this.running = true;
    this.abort = new AbortController();
    try {
      await this.loop(this.abort.signal);
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        this.messages.push({ id: uid(), role: "activity", text: "Stopped." });
      } else {
        const msg =
          e instanceof LlmError ? e.message : `Something went wrong: ${(e as Error).message}`;
        this.messages.push({ id: uid(), role: "error", text: msg });
        if (e instanceof LlmError && e.kind === "unreachable")
          this.connection = { ...this.connection, status: "down", message: msg };
      }
    } finally {
      for (const m of this.messages) if (m.role === "assistant") m.streaming = false;
      this.running = false;
      this.abort = null;
      this.persist();
    }
  }

  private context() {
    const state = app.current;
    return {
      state,
      ledger: computeLedger(state, today()),
      events: app.events,
      today: today(),
      time: localTime(),
      newId: uid,
    };
  }

  /** The treasurer's cards, banks and points, with purchases logged on each card. */
  private async moneyContext(): Promise<MoneyContext | null> {
    if (app.role !== "owner") return null;
    try {
      const r = await api<{ accounts: MoneyAccount[]; programs?: MoneyProgram[] }>(
        "/api/admin/money",
      );
      if (!r.accounts.length) return null;
      const groups: { id: string; name: string; state: import("@split/core").GroupState }[] = [];
      for (const g of app.groups) {
        const state = await app.groupSnapshot(g.id);
        if (state) groups.push({ id: g.id, name: state.name || g.name, state });
      }
      const purchases = Object.fromEntries(
        r.accounts.map((a) => [a.id, cardPurchases(a, groups, today())]),
      );
      return {
        accounts: r.accounts,
        programs: r.programs ?? [],
        purchases,
        states: groups.map((g) => g.state),
        today: today(),
      };
    } catch {
      return null;
    }
  }

  private async loop(signal: AbortSignal): Promise<void> {
    const scope = this.scope;
    const wantMoney = scope === "money" || scope === "everything";
    const wantGroup = scope === "group" || scope === "everything";
    const moneyCtx = wantMoney ? await this.moneyContext() : null;
    for (let round = 0; round < MAX_ROUNDS; round++) {
      const ctx = this.context();
      const withTutoring =
        (scope === "tutoring" || scope === "everything") &&
        app.role === "owner" &&
        tutoring.students.length + tutoring.tutors.length > 0;
      const about = SCOPES.find((x) => x.id === scope)!;
      const system: ChatMessage = {
        role: "system",
        content: `${SYSTEM}\n\nThis chat is about: ${about.label} (${about.hint}). Stay within it; if the user asks about something else, say they can switch the chat's scope at the top.${wantGroup ? `\n\nLedger snapshot:\n${snapshotText(ctx)}` : ""}`,
      };
      this.messages.push({
        id: uid(),
        role: "assistant",
        text: "",
        reasoning: "",
        streaming: true,
        thinkingOpen: true,
      });
      const live = this.messages[this.messages.length - 1] as Extract<
        UiMessage,
        { role: "assistant" }
      >;

      const result = await chat(
        this.settings,
        [system, ...this.llm],
        [
          ...(wantGroup ? TOOL_DEFS : []),
          ...(withTutoring ? TUTORING_TOOL_DEFS : []),
          ...(moneyCtx ? MONEY_TOOL_DEFS : []),
        ],
        (u) => {
          aiQueue.streamed(this.job, u.content.length + u.reasoning.length, 1200);
          live.text = u.content;
          live.reasoning = u.reasoning;
        },
        signal,
      );
      live.streaming = false;
      live.ms = result.ms;
      live.reasoning = result.reasoning;
      // A round that only called tools keeps its bubble when it showed reasoning.
      if (!result.content.trim() && !result.reasoning.trim())
        this.messages.splice(this.messages.indexOf(live), 1);
      this.llm.push({
        role: "assistant",
        content: result.content || null,
        tool_calls: result.toolCalls.length
          ? result.toolCalls.map((c) => ({
              id: c.id,
              type: "function" as const,
              function: { name: c.name, arguments: c.arguments || "{}" },
            }))
          : undefined,
      });
      if (result.toolCalls.length === 0) return;

      for (const call of result.toolCalls) {
        let args: unknown;
        try {
          args = call.arguments ? JSON.parse(call.arguments) : {};
        } catch {
          args = null;
        }
        const out =
          args === null
            ? {
                kind: "error" as const,
                label: `Couldn't read the arguments for ${call.name}`,
                message: "Arguments weren't valid JSON.",
              }
            : MONEY_TOOL_NAMES.has(call.name) && moneyCtx
              ? runMoneyTool(call.name, args, moneyCtx)
              : TUTORING_TOOL_NAMES.has(call.name)
                ? runTutoringTool(call.name, args, {
                    students: tutoring.students,
                    tutors: tutoring.tutors,
                    packages: tutoring.packages,
                    entries: tutoring.entries,
                    slots: tutoring.slots,
                    today: ctx.today,
                  })
                : runTool(call.name, args, ctx);
        const prettyArgs = args === null ? call.arguments : JSON.stringify(args, null, 2);
        if (out.kind === "proposal" || out.kind === "api-proposal") {
          this.messages.push({
            id: uid(),
            role: "proposal",
            summary: out.summary,
            commands: out.kind === "proposal" ? out.commands : [],
            ...(out.kind === "api-proposal"
              ? { api: { calls: out.calls, fields: out.fields } }
              : {}),
            status: "pending",
          });
          this.llm.push({
            role: "tool",
            tool_call_id: call.id,
            content: JSON.stringify({
              status: "shown_to_user",
              summary: out.summary,
              note: "The user must tap Yes. Don't say it's done.",
            }),
          });
        } else {
          const payload = out.kind === "result" ? out.data : { error: out.message };
          this.messages.push({
            id: uid(),
            role: "activity",
            text: out.label,
            failed: out.kind === "error",
            tool: {
              name: call.name,
              args: prettyArgs,
              result: clip(JSON.stringify(payload, null, 2), 4000),
            },
          });
          this.llm.push({ role: "tool", tool_call_id: call.id, content: JSON.stringify(payload) });
        }
      }
    }
    this.messages.push({
      id: uid(),
      role: "activity",
      text: "Stopped after 6 lookups. Ask again more specifically.",
    });
  }

  /** You tapped Yes: apply the proposal through the normal outbox (history + undo apply). */
  async confirm(id: string): Promise<void> {
    const card = this.messages.find((m) => m.id === id);
    if (!card || card.role !== "proposal" || card.status !== "pending") return;
    const edits = ($state.snapshot(card.edits) ?? {}) as Record<string, string>;
    if (card.api) return this.confirmApi(card, edits);
    const fixed = applyFieldEdits($state.snapshot(card.commands) as Command[], edits);
    if (!fixed.ok) {
      app.toast(fixed.message, "error");
      return;
    }
    const before = Object.fromEntries(proposalFields(card.commands).map((f) => [f.key, f]));
    const changed = Object.entries(edits)
      .filter(([k, v]) => before[k] && before[k].value !== v.trim())
      .map(([k, v]) => `${before[k]!.label} ${before[k]!.value} → ${v.trim()}`);
    for (const cmd of fixed.commands) {
      const err = await app.do_(cmd);
      if (err) {
        card.status = "failed";
        card.error = err;
        this.llm.push({ role: "user", content: `[I tapped Yes but it failed: ${err}]` });
        this.persist();
        return;
      }
    }
    card.status = "applied";
    card.commands = fixed.commands;
    const note = changed.length ? ` with my changes: ${changed.join("; ")}` : "";
    this.llm.push({
      role: "user",
      content: `[I tapped Yes and it was saved: ${card.summary}${note}]`,
    });
    this.persist();
  }

  private async confirmApi(
    card: Extract<UiMessage, { role: "proposal" }>,
    edits: Record<string, string>,
  ): Promise<void> {
    const plan = $state.snapshot(card.api)!;
    const edited = applyApiEdits(plan.calls, edits);
    const fixed = edited.ok
      ? { ok: true as const, calls: syncPaymentBalance(edited.calls, plan.calls) }
      : edited;
    if (!fixed.ok) {
      app.toast(fixed.message, "error");
      return;
    }
    const before = Object.fromEntries(plan.fields.map((f) => [f.key, f]));
    const changed = Object.entries(edits)
      .filter(([k, v]) => before[k] && before[k].value !== v.trim())
      .map(([k, v]) => `${before[k]!.label} ${before[k]!.value} → ${v.trim()}`);
    try {
      for (const c of fixed.calls)
        await api(c.path ?? `/api/tutoring/${c.table}`, { body: c.body });
    } catch (e) {
      card.status = "failed";
      card.error = (e as Error).message;
      this.llm.push({ role: "user", content: `[I tapped Yes but it failed: ${card.error}]` });
      this.persist();
      await tutoring.load();
      return;
    }
    if (fixed.calls.some((c) => c.table !== "money")) await tutoring.load();
    card.status = "applied";
    card.api = { calls: fixed.calls, fields: plan.fields };
    const note = changed.length ? ` with my changes: ${changed.join("; ")}` : "";
    this.llm.push({
      role: "user",
      content: `[I tapped Yes and it was saved: ${card.summary}${note}]`,
    });
    this.persist();
  }

  decline(id: string) {
    const card = this.messages.find((m) => m.id === id);
    if (!card || card.role !== "proposal" || card.status !== "pending") return;
    card.status = "declined";
    this.llm.push({ role: "user", content: `[I tapped No: ${card.summary}]` });
    this.persist();
  }
}

export const assistant = new Assistant();
